import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Button,
  IconButton,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const CrosstabsStep5 = ({ 
  projectId,
  selectedDataset,
  responseLabelsTable,
  questionIdField,
  optionTextField,
  responseField,
  parentQuestionIdField,
  selectedQuestions,
  selectedRowQuestions = [],
  selectedColumnQuestions = [],
  responseOrders,
  setResponseOrders,
  hiddenResponses,
  setHiddenResponses,
  collapses,
  setCollapses,
  setFormattedCrosstabsData,
  onNext,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedQuestionType, setSelectedQuestionType] = useState(null); // 'row' or 'column'
  const [responseOptions, setResponseOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Collapse tab state
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [editingGroupIndex, setEditingGroupIndex] = useState(null);

  // State for expanded question text
  const [expandedQuestionText, setExpandedQuestionText] = useState({});

  // Refs for height calculation
  const sidebarRef = useRef(null);
  const tabContentRef = useRef(null);

  // Helper function to determine if a question is in rows, columns, or both
  const getQuestionType = (questionId) => {
    const isInRows = selectedRowQuestions.some(q => q.q_id === questionId);
    const isInColumns = selectedColumnQuestions.some(q => q.q_id === questionId);
    
    if (isInRows && isInColumns) return 'both';
    if (isInRows) return 'row';
    if (isInColumns) return 'column';
    return 'unknown';
  };

  // Helper component for truncated text with expand functionality
  const TruncatedText = ({ text, questionId, maxLength = 120 }) => {
    const isExpanded = expandedQuestionText[questionId];
    const shouldTruncate = text && text.length > maxLength;
    const displayText = shouldTruncate && !isExpanded ? text.substring(0, maxLength) + '...' : text;

    const toggleExpanded = () => {
      setExpandedQuestionText(prev => ({
        ...prev,
        [questionId]: !prev[questionId]
      }));
    };

    return (
      <Box>
        <Typography variant="body2" component="span">
          {displayText}
        </Typography>
        {shouldTruncate && (
          <Button
            size="small"
            onClick={toggleExpanded}
            sx={{ 
              minWidth: 'auto', 
              p: 0.5, 
              ml: 0.5, 
              fontSize: '0.75rem',
              textTransform: 'none',
              color: 'primary.main'
            }}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </Box>
    );
  };

  // Helper function to create question type chip
  const QuestionTypeChip = ({ questionType }) => {
    const getTypeColor = (type) => {
      switch (type?.toLowerCase()) {
        case 'grid': return 'primary';
        case 'multiple': return 'secondary';
        case 'single': return 'default';
        case 'segment': return 'warning';
        case 'rank': return 'info';
        default: return 'default';
      }
    };

    return (
      <Chip
        label={questionType || 'unknown'}
        size="small"
        color={getTypeColor(questionType)}
        variant="outlined"
        sx={{ 
          fontSize: '0.65rem',
          height: '20px',
          '& .MuiChip-label': {
            px: 1
          },
          '&:hover': {
            backgroundColor: 'inherit',
            boxShadow: 'none'
          },
          '&:focus': {
            backgroundColor: 'inherit',
            boxShadow: 'none'
          },
          cursor: 'default'
        }}
      />
    );
  };

  // Filter questions to show only parent questions (grid, multiple) and individual questions
  // Returns array of {question, type} objects where type is 'row' or 'column'
  const getParentQuestions = () => {
    if (!selectedQuestions || selectedQuestions.length === 0) return [];
    
    const parentQuestionsMap = new Map(); // Map to track unique questions
    
    selectedQuestions.forEach(question => {
      // If this question has children (is a parent), add it to parent list
      const hasChildren = selectedQuestions.some(q => 
        q.p_id === question.q_id
      );
      
      if (hasChildren || (question.q_type === 'grid' || question.q_type === 'multiple') && !question.p_id) {
        // Only add if not already added
        if (!parentQuestionsMap.has(question.q_id)) {
          parentQuestionsMap.set(question.q_id, question);
        }
      } else if (!question.p_id) {
        parentQuestionsMap.set(question.q_id, question);
      }
    });
    
    return Array.from(parentQuestionsMap.values());
  };

  const allParentQuestions = getParentQuestions();
  
  // Filter out parent questions that have no children with valid response options
  // This needs to be computed based on responseOptions state
  const getValidParentQuestions = () => {
    if (!responseOptions || Object.keys(responseOptions).length === 0) {
      return allParentQuestions; // Show all initially, will filter after data loads
    }
    
    return allParentQuestions.filter(parentQuestion => {
      // For parent questions (grid, multiple), check if any children have response options
      const children = selectedQuestions.filter(sq => sq.p_id === parentQuestion.q_id);
      
      if (children.length > 0) {
        // Check if any child has response options after filtering
        const hasValidChildren = children.some(child => 
          responseOptions[child.q_id] && responseOptions[child.q_id].length > 0
        );
        return hasValidChildren;
      } else {
        // For non-parent questions, check if they have response options directly
        const hasOptions = responseOptions[parentQuestion.q_id] && responseOptions[parentQuestion.q_id].length > 0;
        return hasOptions;
      }
    });
  };
  
  const validQuestions = getValidParentQuestions();
  
  // Create separate sidebar entries for row and column questions
  const parentQuestions = [];
  validQuestions.forEach(question => {
    const isInRows = selectedRowQuestions.some(q => q.q_id === question.q_id);
    const isInColumns = selectedColumnQuestions.some(q => q.q_id === question.q_id);
    
    if (isInRows) {
      parentQuestions.push({ question, type: 'row' });
    }
    if (isInColumns) {
      parentQuestions.push({ question, type: 'column' });
    }
  });
  
  // Auto-select first question when parentQuestions is populated
  useEffect(() => {
    if (parentQuestions.length > 0 && !selectedQuestionId) {
      const firstEntry = parentQuestions[0];
      setSelectedQuestionId(firstEntry.question.q_id);
      setSelectedQuestionType(firstEntry.type);
    }
  }, [parentQuestions.length, selectedQuestionId, responseOptions, loading]); // Include responseOptions and loading to trigger when data is ready


  // Fetch response options when component mounts
  useEffect(() => {
    const fetchResponseOptions = async () => {
      
      // Wait for selectedQuestions to be loaded (either from props or sessionStorage)
      if (!selectedQuestions || selectedQuestions.length === 0) {
        console.log('Step 4: Waiting for selectedQuestions to be loaded...', {
          selectedQuestions,
          selectedQuestionsLength: selectedQuestions?.length
        });
        setLoading(false);
        return;
      }
      
      // Don't refetch if we already have response options for the current questions
      const currentQuestionIds = selectedQuestions.map(q => q.q_id).sort();
      const existingQuestionIds = Object.keys(responseOptions).sort();
      const hasResponseOptions = Object.keys(responseOptions).length > 0;
      
      // Check if we have response options for all the questions we need
      // (current questions might include parents that don't need response options)
      const questionsNeedingOptions = selectedQuestions.filter(q => 
        q.q_type !== 'grid' || !selectedQuestions.some(sq => sq.p_id === q.q_id)
      ).map(q => q.q_id).sort();
      
      const hasAllNeededOptions = questionsNeedingOptions.every(qId => 
        existingQuestionIds.includes(qId)
      );
      
      if (hasResponseOptions && hasAllNeededOptions) {
        setLoading(false);
        return;
      }

      // More detailed validation with specific error messages
      if (!projectId) {
        setError('Project ID is missing. Please go back to Step 1.');
        setLoading(false);
        return;
      }
      
      // Handle different possible data structures for validation
      const datasetId = selectedDataset?.value || selectedDataset?.id || selectedDataset;
      const tableId = responseLabelsTable?.value || responseLabelsTable?.id || responseLabelsTable;
      
      // TEMPORARY DEBUG: Check if we can find the data in sessionStorage as fallback
      if (!datasetId || !tableId) {
        try {
          const savedData = sessionStorage.getItem('crosstabFormData');
          if (savedData) {
            const data = JSON.parse(savedData);
          }
        } catch (e) {
        }
      }
      
      if (!datasetId) {
        setError('Dataset selection is missing. Please go back to Step 1 and select a dataset.');
        setLoading(false);
        return;
      }
      
      if (!tableId) {
        setError('Response labels table is missing. Please go back to Step 1 and select a response labels table.');
        setLoading(false);
        return;
      }
      
      if (parentQuestions.length === 0) {
        setError('No questions selected. Please go back to Step 3 and select some questions.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get unique question IDs (for grid and multiple questions, we need to get ALL children, not just first)
        const questionIds = [];
        const seenQuestionIds = new Set(); // Track to avoid duplicates
        
        parentQuestions.forEach(entry => {
          const q = entry.question; // Extract the question object from {question, type}
          // If this is a parent question (grid, multiple), get all its children
          const children = selectedQuestions.filter(sq => sq.p_id === q.q_id);
          if (children.length > 0) {
            // For parent questions with children, we need all child IDs to get response options
            children.forEach(child => {
              if (!seenQuestionIds.has(child.q_id)) {
                questionIds.push(child.q_id);
                seenQuestionIds.add(child.q_id);
              }
            });
          } else {
            // For questions without children, use the question itself
            if (!seenQuestionIds.has(q.q_id)) {
              questionIds.push(q.q_id);
              seenQuestionIds.add(q.q_id);
            }
          }
        });
        

        // Use the same datasetId and tableId from validation above
        

        const params = new URLSearchParams({
          project_id: projectId,
          dataset_id: datasetId,
          table_id: tableId,
          question_id_field: questionIdField,
          option_text_field: optionTextField,
          response_field: responseField,
          parent_question_id_field: parentQuestionIdField,
        });

        // Add selected questions as array parameters
        questionIds.forEach(qId => {
          params.append('selected_questions[]', qId);
        });

        const response = await fetch(`/crosstabs/get-response-options/?${params}`);
        
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        // Add validation for empty response
        if (!data.response_options || Object.keys(data.response_options).length === 0) {
          console.warn('No response options returned from API');
          const errorMsg = `No response options found for question "${selectedQuestionId || questionIds[0]}" in the VVL table. 

          Possible issues:
          1. The question ID "${selectedQuestionId || questionIds[0]}" might not exist in the VVL table "${responseLabelsTable?.value || tableId}"
          2. The field mappings might be incorrect:
            - Question ID field: "${questionIdField}"
            - Response field: "${responseField}"
            - Option text field: "${optionTextField}"
          3. The VVL table might be empty or have different column names
          4. For multiple choice questions, make sure you have both "0" (Not Selected) AND "1" (Selected) values

          Please check your data source configuration in Steps 1 and 2.`;
          
          setError(errorMsg);
          return;
        }

        // Filter out ignored values based on question type (following crosstabs library logic)
        const filteredResponseOptions = {};
        Object.entries(data.response_options || {}).forEach(([qId, responses]) => {
          // Find the question to get its type
          const question = selectedQuestions.find(q => q.q_id === qId);
          const questionType = question?.q_type;
          
          // Define ignore values based on crosstabs library logic
          const ignoreValues = {
            'multiple': ['0', '0.0', '88888', '88888.0', '99999', '99999.0'],
            'rank': ['88888', '88888.0', '99999', '99999.0', '995', '995.0'],
            'grid': ['88888', '88888.0', '99999', '99999.0'],
            'single': ['88888', '88888.0', '99999', '99999.0'],
            'default': ['88888', '88888.0', '99999', '99999.0']
          };
          
          const valuesToIgnore = ignoreValues[questionType] || ignoreValues['default'];
          
          // Filter out ignored values
          const filteredResponses = responses.filter(response => 
            !valuesToIgnore.includes(response.value)
          );
          
          if (filteredResponses.length > 0) {
            filteredResponseOptions[qId] = filteredResponses;
          } else {
            console.warn(`All response options filtered out for question ${qId} (${questionType})`);
          }
        });

        // Check if all response options were filtered out
        if (Object.keys(filteredResponseOptions).length === 0) {
          console.warn('All response options were filtered out');
          const errorMsg = `No valid response options found after filtering. 

          This typically happens with multiple choice questions that only have "Not Selected" (0/0.0) values in the VVL table.

          For multiple choice questions, you need:
          - "0" or "0.0" → "Not Selected" (filtered out - not shown in crosstabs)
          - "1" or "1.0" → "Selected" (used in crosstabs)

          Please add the "Selected" values to your VVL table.`;
          
          setError(errorMsg);
          return;
        }

        setResponseOptions(filteredResponseOptions);

        // Debug: Log what we actually got
        Object.entries(filteredResponseOptions || {}).forEach(([qId, responses]) => {
        });

        // Validate that grid children have the same responses
        validateGridChildrenResponses(data.response_options || {});

        // Set default selected question if none selected or if current selection is no longer valid
        const validQuestions = getValidParentQuestions();
        if ((!selectedQuestionId || !validQuestions.find(q => q.q_id === selectedQuestionId)) && validQuestions.length > 0) {
          // Auto-select the first question and determine its type (row or column)
          const firstQuestion = validQuestions[0];
          const isInRows = selectedRowQuestions.some(q => q.q_id === firstQuestion.q_id);
          setSelectedQuestionId(firstQuestion.q_id);
          setSelectedQuestionType(isInRows ? 'row' : 'column');
        }

      } catch (err) {
        console.error('Error fetching response options:', err);
        setError(`Failed to load response options: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchResponseOptions();
  }, [projectId, selectedDataset, responseLabelsTable, selectedQuestions, questionIdField, optionTextField, responseField, parentQuestionIdField]);

  // Reset to Order tab if user selects a column question while on Collapse tab
  useEffect(() => {
    if (activeTab === 1 && selectedQuestionType === 'column') {
      setActiveTab(0);
    }
  }, [selectedQuestionId, selectedQuestionType, activeTab]); // Depend on questionId, questionType, and activeTab to properly reset when switching to column questions

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleQuestionSelect = (questionId, questionType) => {
    setSelectedQuestionId(questionId);
    setSelectedQuestionType(questionType);
    setNewGroupName('');
    setSelectedResponses([]);
    setEditingGroupIndex(null);
  };
  
  // Helper to get state key combining question ID and type
  const getStateKey = (questionId, questionType) => {
    return `${questionId}-${questionType}`;
  };
  
  // Helper to get current state key
  const currentStateKey = selectedQuestionId && selectedQuestionType 
    ? getStateKey(selectedQuestionId, selectedQuestionType) 
    : null;

  // Get current responses for selected question (memoized to prevent unnecessary re-renders)
  const currentResponses = useMemo(() => {
    if (!selectedQuestionId || !currentStateKey) return [];
    
    // Find the selected question details
    
    // For parent questions (grid, multiple), get the first child's responses (they should all have the same options)
    const children = selectedQuestions.filter(sq => sq.p_id === selectedQuestionId);
    const questionIdToUse = children.length > 0 ? children[0].q_id : selectedQuestionId;
    const responses = responseOptions[questionIdToUse] || [];
    
    // Apply current order if it exists (using state key)
    const currentOrder = responseOrders[currentStateKey];
    if (currentOrder) {
      const orderedResponses = [];
      currentOrder.forEach(value => {
        const response = responses.find(r => r.value === value);
        if (response) {
          orderedResponses.push(response);
        }
      });
      // Add any responses not in the order (new ones)
      responses.forEach(response => {
        if (!currentOrder.includes(response.value)) {
          orderedResponses.push(response);
        }
      });
      return orderedResponses;
    }
    
    return responses;
  }, [selectedQuestionId, currentStateKey, selectedQuestions, responseOptions, responseOrders[currentStateKey]]);

  // Get current responses for selected question (kept for backward compatibility)
  const getCurrentResponses = () => {
    return currentResponses;
  };

  // Check if response is hidden
  const isResponseHidden = (questionId, responseValue) => {
    // For the current question, use currentStateKey; otherwise fall back to questionId for compatibility
    const key = questionId === selectedQuestionId && currentStateKey ? currentStateKey : questionId;
    return hiddenResponses[key]?.includes(responseValue) || false;
  };

  // Check if the selected question is a column question (collapse not allowed for columns)
  const isSelectedQuestionColumn = () => {
    // Check if the currently selected instance is a column (not just if the question exists in columns)
    return selectedQuestionType === 'column';
  };

  // Move response up in the order
  const moveResponseUp = (index) => {
    if (index > 0 && currentStateKey) {
      const currentOrder = responseOrders[currentStateKey] || currentResponses.map(r => r.value);
      const newOrder = [...currentOrder];
      [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
      setResponseOrders(prev => ({
        ...prev,
        [currentStateKey]: newOrder
      }));
    }
  };

  // Move response down in the order
  const moveResponseDown = (index) => {
    if (index < currentResponses.length - 1 && currentStateKey) {
      const currentOrder = responseOrders[currentStateKey] || currentResponses.map(r => r.value);
      const newOrder = [...currentOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setResponseOrders(prev => ({
        ...prev,
        [currentStateKey]: newOrder
      }));
    }
  };

  // Collapse/Group helper functions
  const getCurrentCollapses = () => {
    return currentStateKey ? collapses[currentStateKey] || [] : [];
  };

  const addCollapse = () => {
    if (!newGroupName.trim() || selectedResponses.length === 0 || !currentStateKey) return;
    
    const newCollapse = {
      label: newGroupName.trim(),
      values: [...selectedResponses]
    };
    
    setCollapses(prev => ({
      ...prev,
      [currentStateKey]: [...getCurrentCollapses(), newCollapse]
    }));
    
    setNewGroupName('');
    setSelectedResponses([]);
  };

  const removeCollapse = (collapseIndex) => {
    if (!currentStateKey) return;
    const currentCollapses = getCurrentCollapses();
    const newCollapses = currentCollapses.filter((_, index) => index !== collapseIndex);
    
    setCollapses(prev => ({
      ...prev,
      [currentStateKey]: newCollapses
    }));
  };

  const startEditingGroup = (index) => {
    const currentCollapses = getCurrentCollapses();
    const groupToEdit = currentCollapses[index];
    
    setEditingGroupIndex(index);
    setNewGroupName(groupToEdit.label);
    setSelectedResponses([...groupToEdit.values]);
  };

  const saveEditingGroup = () => {
    if (!newGroupName.trim() || selectedResponses.length === 0 || !currentStateKey) return;

    const currentCollapses = getCurrentCollapses();
    const updatedCollapses = [...currentCollapses];
    updatedCollapses[editingGroupIndex] = {
      label: newGroupName.trim(),
      values: [...selectedResponses]
    };

    setCollapses(prev => ({
      ...prev,
      [currentStateKey]: updatedCollapses
    }));

    setEditingGroupIndex(null);
    setNewGroupName('');
    setSelectedResponses([]);
  };

  const cancelEditingGroup = () => {
    setEditingGroupIndex(null);
    setNewGroupName('');
    setSelectedResponses([]);
  };

  const toggleResponseSelection = (responseValue) => {
    setSelectedResponses(prev => {
      if (prev.includes(responseValue)) {
        return prev.filter(v => v !== responseValue);
      } else {
        return [...prev, responseValue];
      }
    });
  };

  const getAvailableResponses = () => {
    return currentResponses; // Show all responses, don't filter out used ones
  };

  // Generate the output format needed for crosstabs library submission
  const generateCrosstabsFormat = () => {
    const formattedColumns = [];
    
    
    // Generate value_collapses format
    // Note: Collapses now use state keys (questionId-type), but we need to map them back to just questionId
    const formattedCollapses = {};
    
    // First, build a map of parent questions to their children from the rows/columns arrays
    const parentToChildrenMap = {};
    
    // Helper to find children for a parent question from rows/columns
    const findChildrenInArray = (parentId, questionsArray) => {
      const children = [];
      questionsArray.forEach(q => {
        // Check if this question's ID starts with the parent ID followed by underscore
        // e.g., MES_H2H_25_PL_1, MES_H2H_25_PL_2 are children of MES_H2H_25_PL
        if (q.q_id && q.q_id.startsWith(parentId + '_')) {
          children.push(q.q_id);
        }
      });
      return children;
    };
    
    Object.entries(collapses).forEach(([stateKey, questionCollapses]) => {
      if (questionCollapses && questionCollapses.length > 0) {
        // Skip empty keys
        if (!stateKey || stateKey.trim() === '') {
          console.warn('Skipping collapse with empty state key');
          return;
        }
        
        // Extract question ID and type from state key (format: "questionId-row" or "questionId-column")
        const parts = stateKey.split('-');
        const lastPart = parts[parts.length - 1];
        
        // Validate that we have a proper state key with row/column suffix
        if (lastPart !== 'row' && lastPart !== 'column') {
          console.warn(`Invalid state key format: ${stateKey}. Expected format: "questionId-row" or "questionId-column"`);
          return;
        }
        
        const questionId = parts.slice(0, -1).join('-');
        const stateKeyType = lastPart; // 'row' or 'column'
        
        console.log(`Processing collapses for ${questionId}:`, {
          stateKey,
          questionId,
          stateKeyType,
          questionCollapses
        });
        
        // Only look for children in the matching question list (row or column)
        // This prevents applying the same collapse twice when a grid question is used as both row and column
        const questionsToSearch = stateKeyType === 'row' ? selectedRowQuestions : selectedColumnQuestions;
        
        const children = findChildrenInArray(questionId, questionsToSearch);
        
        console.log(`  Found ${children.length} children in ${stateKeyType || 'both'}:`, children);
        
        if (children.length > 0) {
          // This is a parent question - apply collapses to all children
          // The UI shows collapses on the parent for simplicity, but the library needs them on each child
          children.forEach(childId => {
            console.log(`  Applying collapses to child: ${childId}`);
            if (formattedCollapses[childId]) {
              formattedCollapses[childId] = [...formattedCollapses[childId], ...questionCollapses];
            } else {
              formattedCollapses[childId] = [...questionCollapses];
            }
          });
        } else {
          // This is a regular question - use the question ID directly
          console.log(`  No children found, using question ID directly: ${questionId}`);
          if (formattedCollapses[questionId]) {
            formattedCollapses[questionId] = [...formattedCollapses[questionId], ...questionCollapses];
          } else {
            formattedCollapses[questionId] = questionCollapses;
          }
        }
      }
    });
    
    console.log('Final formattedCollapses:', formattedCollapses);
    
    const formattedRows = [];
    
    // Process column questions - preserve the order from selectedColumnQuestions
    const columnQuestions = selectedColumnQuestions.map(cq => {
      // Find the parent question details for this column question
      const entry = parentQuestions.find(pq => pq.question.q_id === cq.q_id);
      return entry ? entry.question : cq;
    }).filter(Boolean);
    
    // Process row questions - preserve the order from selectedRowQuestions
    const rowQuestions = selectedRowQuestions.map(rq => {
      // Find the parent question details for this row question  
      const entry = parentQuestions.find(pq => pq.question.q_id === rq.q_id);
      return entry ? entry.question : rq;
    }).filter(Boolean);
    
    // Helper function to process questions for a specific type (row or column)
    const processQuestionsForType = (questionsToProcess, targetArray, type) => {
      questionsToProcess.forEach(question => {
        const questionId = question.q_id;
        const stateKey = getStateKey(questionId, type);
        
        // Check if this is a parent question with children
        const children = selectedQuestions.filter(sq => sq.p_id === questionId);
        
        // Skip parent questions that have children - only process the children themselves
        // The children will be processed separately and appear in the output
        if (children.length > 0) {
          console.log(`Skipping parent question ${questionId} - will use its children instead`);
          return;
        }
        
        // Check if this is a child question (has a parent)
        const parentQuestion = selectedQuestions.find(sq => sq.q_id === question.p_id);
        const parentStateKey = parentQuestion ? getStateKey(parentQuestion.q_id, type) : null;
        
        // Get the configured responses for this question using the state key
        // For child questions, also check the parent's state key since UI operations happen on the parent
        const currentOrder = responseOrders[stateKey] || (parentStateKey ? responseOrders[parentStateKey] : null);
        const hiddenList = hiddenResponses[stateKey] || (parentStateKey ? hiddenResponses[parentStateKey] : null) || [];
        
        // Get the response options for this question
        const availableResponses = responseOptions[questionId] || [];
        
        // Build the responses array in the correct order, excluding hidden ones
        let orderedResponses = [];
        
        if (currentOrder && currentOrder.length > 0) {
          // Use the custom order
          currentOrder.forEach(value => {
            if (!hiddenList.includes(value)) {
              orderedResponses.push(value);
            }
          });
          
          // Add any responses not in the custom order (but not hidden)
          availableResponses.forEach(response => {
            if (!currentOrder.includes(response.value) && !hiddenList.includes(response.value)) {
              orderedResponses.push(response.value);
            }
          });
        } else {
          // Use default order, excluding hidden ones
          availableResponses.forEach(response => {
            if (!hiddenList.includes(response.value)) {
              orderedResponses.push(response.value);
            }
          });
        }
        
        // Create the entry for crosstabs library
        const entry = {
          column_name: questionId
        };
        
        // Only add responses array if we have custom ordering/filtering
        if (orderedResponses.length > 0 && 
            (currentOrder || hiddenList.length > 0)) {
          entry.responses = orderedResponses;
        }
        
        targetArray.push(entry);
      });
    };
    
    // Process row questions separately
    processQuestionsForType(rowQuestions, formattedRows, 'row');
    
    // Process column questions separately  
    processQuestionsForType(columnQuestions, formattedColumns, 'column');

    const result = {
      columns: formattedColumns,
      rows: formattedRows,
      value_collapses: Object.keys(formattedCollapses).length > 0 ? formattedCollapses : undefined
    };
    
    return result;
  };

  const generateCrosstabConfigForStep5 = () => {
    const crosstabsData = generateCrosstabsFormat();
    
    // For reuse/edit functionality, we need to save the ORIGINAL question selections (including parent questions)
    // because Steps 3 & 4 show parent questions, not individual children
    const originalRowSelections = selectedRowQuestions.map(q => q.q_id);
    const originalColumnSelections = selectedColumnQuestions.map(q => q.q_id);
    
    // Include the raw state (with state keys) for reuse/edit functionality
    // This allows Step 5 to restore the UI state when reusing a crosstab
    const configWithRawState = {
      ...crosstabsData,
      // Raw state needed to restore Step 5 UI
      response_orders: responseOrders,
      hidden_responses: hiddenResponses,
      value_collapses_raw: collapses,
      // Save original question selections (including parent questions) for Steps 3 & 4 reuse
      original_row_selections: originalRowSelections,
      original_column_selections: originalColumnSelections,
    };
    
    console.log('Step 4 - User configurations for Step 5:', {
      responseOrders,
      hiddenResponses,
      collapses,
      crosstabsData,
      configWithRawState
    });
    
    return configWithRawState;
  };

  const handleNext = () => {
    try {
      const crosstabsConfig = generateCrosstabConfigForStep5();
      
      if (setFormattedCrosstabsData) {
        setFormattedCrosstabsData(crosstabsConfig);
      }
      
      if (onNext) onNext();
    } catch (error) {
      console.error('Error in handleNext:', error);
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
  };

  // Toggle response visibility
  const toggleResponseVisibility = (questionId, responseValue) => {
    // For the current question, use currentStateKey; otherwise fall back to questionId for compatibility
    const key = questionId === selectedQuestionId && currentStateKey ? currentStateKey : questionId;
    const currentHidden = hiddenResponses[key] || [];
    const isHidden = currentHidden.includes(responseValue);
    
    if (isHidden) {
      // Show response - remove from hidden list
      const newHidden = currentHidden.filter(v => v !== responseValue);
      setHiddenResponses(prev => ({
        ...prev,
        [key]: newHidden
      }));
    } else {
      // Hide response - add to hidden list
      setHiddenResponses(prev => ({
        ...prev,
        [key]: [...currentHidden, responseValue]
      }));
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    // Don't immediately clear dragOverIndex to avoid flicker
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex || !currentStateKey) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...currentResponses];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    
    // Insert at new position
    const insertIndex = draggedIndex < dropIndex ? dropIndex - 1 : dropIndex;
    newOrder.splice(insertIndex, 0, draggedItem);
    
    // Update response order
    const newOrderValues = newOrder.map(r => r.value);
    setResponseOrders(prev => ({
      ...prev,
      [currentStateKey]: newOrderValues
    }));
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };


  // Validate that grid children have the same response options
  const validateGridChildrenResponses = (responseOptionsData) => {
    const warnings = [];
    
    // Group questions by parent
    const parentGroups = {};
    selectedQuestions.forEach(question => {
      if (question.p_id) {
        if (!parentGroups[question.p_id]) {
          parentGroups[question.p_id] = [];
        }
        parentGroups[question.p_id].push(question.q_id);
      }
    });

    // Check each parent group
    Object.entries(parentGroups).forEach(([parentId, childIds]) => {
      if (childIds.length > 1) {
        // Get response options for each child
        const childResponses = childIds.map(childId => {
          const responses = responseOptionsData[childId] || [];
          return responses.map(r => r.value).sort();
        });

        // Check if all children have the same response options
        const firstChildResponses = childResponses[0] || [];
        const allMatch = childResponses.every(responses => 
          responses.length === firstChildResponses.length &&
          responses.every((value, index) => value === firstChildResponses[index])
        );

        if (!allMatch) {
          console.warn(`Grid question ${parentId} has children with different response options:`, childResponses);
          warnings.push(`Grid question ${parentId} children have inconsistent response options`);
        }
      }
    });

    if (warnings.length > 0) {
      console.warn('Validation warnings:', warnings);
      // You could set these warnings in state to display to the user if needed
    }

    return warnings;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading response options...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
            {error}
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>
    );
  }

  if (parentQuestions.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          No questions selected. Please go back to Step 3 and select some questions.
        </Alert>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', px: 4 }}>
      {/* Step Title */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          Order responses of each question
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
          Optional: Customize responses - You can customize how responses appear in your crosstab by reordering or collapsing them into groups
        </Typography>
      </Box>

      {/* Main Content Box */}
      <Paper sx={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 0, mb: 4, p: 0, minHeight: 'auto', border: 1, borderColor: 'divider', overflow: 'hidden' }}>
        {/* Left Sidebar - Questions List */}
        <Box sx={{ borderRight: 3, borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
          <List 
            ref={sidebarRef}
            sx={{ 
              py: 0, 
              overflow: 'auto',
              flex: '1 1 0',
              minHeight: 0
            }}
          >
          {parentQuestions.map((entry, index) => {
            const { question, type } = entry;
            const stateKey = getStateKey(question.q_id, type);
            const isSelected = selectedQuestionId === question.q_id && selectedQuestionType === type;
            
            return (
            <ListItem
              key={stateKey}
              component="button"
              selected={isSelected}
              onClick={() => handleQuestionSelect(question.q_id, type)}
              sx={{
                bgcolor: isSelected 
                  ? 'primary.main' 
                  : index % 2 === 0 ? 'background.default' : 'secondary.light',
                color: isSelected ? 'primary.contrastText' : 'inherit',
                '&.Mui-selected': {
                  bgcolor: 'primary.main !important',
                  color: 'primary.contrastText !important',
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'inherit',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiTypography-root': {
                    color: 'inherit',
                  },
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                  cursor: 'pointer',
                  color: 'primary.main',
                  '& .MuiTypography-root': {
                    color: 'primary.main',
                  },
                  '& .MuiChip-root': {
                    bgcolor: 'transparent',
                    color: 'primary.main',
                    borderColor: 'primary.main',
                  },
                },
                ...(isSelected && {
                  '& .MuiChip-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'inherit',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiTypography-root': {
                    color: 'inherit',
                  },
                }),
                borderBottom: 1,
                borderColor: 'divider',
                borderRight: '1px solid transparent', 
                borderLeft: '1px solid transparent',
                borderTop: index === 0 ? 'none' : '1px solid transparent',
                px: 2,
                py: 1.5,
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column', py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <QuestionTypeChip questionType={question?.q_type} />
                      <Chip 
                        label={type === 'row' ? 'Row' : 'Column'} 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.6rem', 
                          height: '18px',
                          bgcolor: type === 'row' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(237, 108, 2, 0.1)',
                          borderColor: type === 'row' ? 'primary.main' : '#ED6C02',
                          color: type === 'row' ? 'primary.main' : '#ED6C02'
                        }} 
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#008080' }}>
                        ID: {question?.q_id}
                      </Typography>
                    </Box>
                    <TruncatedText 
                      text={question?.q_text || ''} 
                      questionId={`step4-${question?.q_id}`}
                      maxLength={80}
                    />
                  </Box>
                }
              />
            </ListItem>
            );
          })}
          </List>
        </Box>

        {/* Right Content Area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
        {selectedQuestionId && (
          <>
            {/* Tabs */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'auto' }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    color: '#cc5500', // Dark orange for inactive tabs
                    '&:hover': {
                      color: '#ff6600', // Lighter orange on hover for better contrast
                      backgroundColor: 'rgba(204, 85, 0, 0.08)', // Light orange background on hover
                    },
                    '&.Mui-selected': {
                      color: 'primary.main', // Active tab uses theme primary color
                    },
                  },
                }}
              >
                <Tab label="Order" />
                {!isSelectedQuestionColumn() && <Tab label="Collapse" />}
              </Tabs>

              {/* Tab Content */}
              <Box ref={tabContentRef} sx={{ minHeight: 'auto' }}>
                {activeTab === 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
                    {/* Question Header */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="h6">
                        {parentQuestions.find(entry => entry.question.q_id === selectedQuestionId)?.question.q_text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {selectedQuestionId}
                      </Typography>
                    </Box>
                    
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Order & Visibility
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Drag to reorder responses. Click the eye icon to hide/show responses in your crosstab.
                    </Typography>
                    
                    <List sx={{ border: 1, borderColor: 'divider', borderRadius: 1, py: 0 }}>
                      {currentResponses.map((response, index) => {
                        const isHidden = isResponseHidden(selectedQuestionId, response.value);
                        const isDragged = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;
                        
                        return (
                          <ListItem
                            key={response.value}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            sx={{
                              bgcolor: index % 2 === 0 ? 'background.default' : 'secondary.light',
                              opacity: isDragged ? 0.5 : 1,
                              borderTop: isDragOver && draggedIndex !== null && draggedIndex !== index 
                                ? '3px solid' 
                                : 'none',
                              borderTopColor: 'primary.main',
                              borderBottom: 1,
                              borderBottomColor: 'divider',
                              cursor: 'grab',
                              '&:active': {
                                cursor: 'grabbing',
                              },
                              ...(isHidden && {
                                opacity: 0.4,
                                bgcolor: 'grey.100',
                              }),
                            }}
                          >
                            <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1 }} />
                            
                            <ListItemText
                              primary={response.label}
                              secondary={`Value: ${response.value}`}
                              primaryTypographyProps={{ 
                                fontSize: '0.9rem',
                                ...(isHidden && { textDecoration: 'line-through' })
                              }}
                              secondaryTypographyProps={{ fontSize: '0.75rem' }}
                            />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                              <IconButton
                                size="small"
                                onClick={() => moveResponseUp(index)}
                                disabled={index === 0}
                              >
                                <ArrowUpIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => moveResponseDown(index)}
                                disabled={index === currentResponses.length - 1}
                              >
                                <ArrowDownIcon />
                              </IconButton>
                            </Box>
                            
                            <IconButton
                              onClick={() => toggleResponseVisibility(selectedQuestionId, response.value)}
                              sx={{ ml: 1 }}
                            >
                              {isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
                {(activeTab === 1 && !isSelectedQuestionColumn()) && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
                    {/* <Typography variant="h4" sx={{ mb: 1 }}>
                      Collapse Responses
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Create groups to combine multiple response options into a single collapse.
                    </Typography> */}
                    {/* Question Header */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="h6">
                        {parentQuestions.find(entry => entry.question.q_id === selectedQuestionId)?.question.q_text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {selectedQuestionId}
                      </Typography>
                    </Box>
                    
                    

                    {/* Existing Collapses */}
                    {getCurrentCollapses().length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                          Current Collapses
                        </Typography>
                        {getCurrentCollapses().map((collapse, index) => (
                          <Paper key={index} sx={{ p: 2, mb: 1, bgcolor: 'secondary.light' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {collapse.label}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => startEditingGroup(index)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => removeCollapse(index)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {collapse.values.map((value) => {
                                const response = currentResponses.find(r => r.value === value);
                                return (
                                  <Chip 
                                    key={value}
                                    label={response ? response.label : value}
                                    size="small"
                                    variant="outlined"
                                  />
                                );
                              })}
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* Create New Group or Edit Group */}
                    <Paper sx={{ 
                      p: 2, 
                      mb: 2, 
                      border: 1, 
                      borderColor: editingGroupIndex !== null ? 'primary.main' : 'primary.main', 
                      borderStyle: editingGroupIndex !== null ? 'solid' : 'dashed' 
                    }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {editingGroupIndex !== null ? 'Edit Collapse' : 'Create New Collapse'}
                      </Typography>
                      
                      <TextField
                        label="Collapse Name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        placeholder="e.g., Approve, Neutral, Disapprove"
                      />

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Select Responses to Group:
                      </Typography>
                      
                      <Box sx={{ 
                        maxHeight: 200, 
                        overflow: 'auto', 
                        border: 1, 
                        borderColor: 'divider', 
                        borderRadius: 1, 
                        bgcolor: 'background.paper'
                      }}>
                        {getAvailableResponses().map((response, index) => (
                          <Box
                            key={response.value}
      sx={{ 
        display: 'flex', 
                              alignItems: 'flex-start',
                              p: 1.5,
                              borderBottom: index < getAvailableResponses().length - 1 ? 1 : 0,
                              borderColor: 'divider',
                              '&:hover': {
                                bgcolor: 'action.hover'
                              },
                              transition: 'background-color 0.2s'
                            }}
                          >
                            <Checkbox
                              checked={selectedResponses.includes(response.value)}
                              onChange={() => toggleResponseSelection(response.value)}
                              size="small"
                              sx={{ mt: -0.5, mr: 1 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {response.label}
      </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Value: {response.value}
      </Typography>
                            </Box>
                          </Box>
                        ))}
                        {getAvailableResponses().length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No responses available.
        </Typography>
      )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={editingGroupIndex !== null ? saveEditingGroup : addCollapse}
                          disabled={!newGroupName.trim() || selectedResponses.length === 0}
                          sx={{ flex: 1 }}
                        >
                          {editingGroupIndex !== null ? 'Save Changes' : 'Create Group'}
                        </Button>
                        {editingGroupIndex !== null && (
                          <Button
                            variant="outlined"
                            onClick={cancelEditingGroup}
                          >
                            Cancel
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
        </Box>
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          sx={{ px: 4, py: 1.5 }}
        >
          Back
        </Button>
        <Button 
          variant="contained" 
          onClick={handleNext}
          size="large"
          sx={{ 
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            backgroundColor: 'grey.600',
            '&:hover': {
              backgroundColor: 'grey.700'
            }
          }}
        >
          Create Crosstab
        </Button>
      </Box>
    </Box>
  );
};

export default CrosstabsStep5;